import RtcClient from '@/lib/RtcClient';
import { FEEDBACK_API_BASE, API_BASE_URL } from '@/config';
import { fetchWithTimeout, retryAsync } from '@/utils/networkUtils';

import logger from '@/utils/logger';

// 请求缓存和去重机制
// const requestCache = new Map<string, { data: any; timestamp: number; ttl: number }>();
// const pendingRequests = new Map<string, Promise<any>>();

/**
 * @brief RTC 相关APIs (通过统一的 /api/rtc/ 前缀)
 */
export const BasicAPIs = [
  {
    action: 'getScenes',
    apiPath: '/api/rtc/getScenes', // 统一路径
    method: 'post',
  },
  {
    action: 'getSceneToken',
    apiPath: '/api/rtc/getSceneToken',
    method: 'post',
  },
  {
    action: 'getSceneSuggestions',
    apiPath: '/api/rtc/getSceneSuggestions',
    method: 'post',
  },
] as const;

/**
 * @brief AIGC APIs (通过统一的 /api/rtc/ 前缀)
 */
export const AigcAPIs = [
  {
    action: 'StartVoiceChat',
    apiPath: '/api/rtc/proxy', // 统一路径
    method: 'post',
  },
  {
    action: 'StopVoiceChat',
    apiPath: '/api/rtc/proxy', // 统一路径
    method: 'post',
  },
] as const;

/**
 * 对话反馈数据接口
 */
export interface ConversationFeedback {
  sessionInfo: {
    startTime: string;
    endTime: string;
    sceneInfo: {
      name: string;
      roomId: string;
      taskId: string;
    };
  };
  conversations: Array<{
    timestamp: string;
    role: 'user' | 'assistant';
    content: string;
    metadata: {
      isInterrupted?: boolean;
      definite?: boolean;
      paragraph?: boolean;
      feedback?: 'like' | 'dislike' | null;
    };
  }>;
  feedbackStats: {
    totalMessages: number;
    userMessages: number;
    assistantMessages: number;
    likeCount: number;
    dislikeCount: number;
    interruptCount: number;
  };
}

/**
 * 发送对话反馈数据到后端
 * @param feedback 反馈数据
 * @returns
 */
export const sendConversationFeedback = async (
  feedback: ConversationFeedback,
  token?: string
): Promise<boolean> => {
  // 使用配置的API基础地址
  const endpoint = `${FEEDBACK_API_BASE}/api/feedback`;

  const maxRetries = 3;
  const retryDelays = [1000, 2000, 4000]; // 递增的重试延迟

  const attemptUpload = async (attempt: number): Promise<boolean> => {
    try {
      // 优先用传入的 token，没有再 fallback 到 RtcClient.basicInfo.token
      const authHeader: Record<string, string> = token
        ? { Authorization: `Bearer ${token}` }
        : RtcClient?.basicInfo?.token
        ? { Authorization: `Bearer ${RtcClient.basicInfo.token}` }
        : {};

      const response = await fetchWithTimeout(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Request-ID': sessionStorage.getItem('RequestID') || '',
          ...authHeader,
        },
        credentials: 'include', // 包含 cookies
        mode: 'cors', // 启用 CORS
        body: JSON.stringify(feedback),
        timeout: 10000,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
      }

      logger.info('[API] 反馈数据发送成功');
      return true;
    } catch (error) {
      logger.warn('[API] 反馈上传失败 (尝试', attempt + 1, '/', maxRetries, ')', error);

      if (attempt >= maxRetries - 1) {
        throw error;
      }

      return retryAsync(() => attemptUpload(attempt + 1), {
        retries: 0,
        delayMs: retryDelays[attempt],
        backoff: false,
      });
    }
  };

  try {
    return await attemptUpload(0);
  } catch (error) {
    logger.error('[API] 反馈上传最终失败', error);
    return false;
  }
};

/**
 * 生成联想问题（使用新的RESTful API v2接口）
 */
export const getLLMSuggestions = async (
  content: string,
  count?: number,
  model?: string,
  history?: Array<{ role: 'user' | 'assistant'; content: string }>
): Promise<{ suggestions: string[]; model: string }> => {
  // 使用新的RESTful API v2接口
  const endpoint = `${API_BASE_URL}/api/v2/suggestions/generate`;
  const resp = await fetchWithTimeout(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ content, count: count ?? 2, model, history }),
    credentials: 'include',
    mode: 'cors',
    timeout: 15000,
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`LLM suggestions failed: ${resp.status} ${text}`);
  }
  return resp.json();
};
